import React from 'react'

import { CopyButton } from '@components/common/CopyButton'
import type { TableHeaders } from '@fourthwall/components'
import {
  Button,
  ButtonClean,
  List,
  PriceString,
  Table,
  useMediaQueryContext,
} from '@fourthwall/components'
import { useCopyToClipboard } from '@fourthwall/components/src/hooks/useCopyToClipboard'
import { getPrice } from '@fourthwall/utils'
import { downloadCsvFile } from '@fourthwall/utils/dist/downloadCsvFile'

import { useCurrentShop } from '../../../../../../hooks/useCurrentShop'
import type { GiftCardGroup } from '../../GiftCard.types'
import * as S from './GiftCardCodesData.styled'

type GiftCardCodesDataProps = {
  giftCards?: GiftCardGroup['giftCards']
}

export const GiftCardCodesData = ({ giftCards }: GiftCardCodesDataProps) => {
  const media = useMediaQueryContext()
  const { currentShop } = useCurrentShop()
  const { copyToClipboard } = useCopyToClipboard({
    toastMessage: 'Gift card codes copied to clipboard',
  })

  const handleCopyCodes = () => {
    const codes = giftCards?.map((giftCard) => giftCard.code).join('\n')

    if (codes) {
      copyToClipboard(codes)
    }
  }

  const handleDownloadCSV = () => {
    const codes = giftCards?.map((giftCard) => ({
      code: giftCard.code,
      initial_value: getPrice(giftCard.balance.initial.value, {
        currency: giftCard.balance.initial.currency,
      }),
      remaining_value: getPrice(giftCard.balance.remaining.value, {
        currency: giftCard.balance.remaining.currency,
      }),
      used_value: getPrice(giftCard.balance.used.value, {
        currency: giftCard.balance.used.currency,
      }),
      balance_page_url: `${currentShop?.baseUri}/gift_cards/${giftCard.id}`,
    }))

    if (codes) {
      downloadCsvFile(codes, 'gift_card_codes', [
        'code',
        'initial_value',
        'remaining_value',
        'used_value',
        'balance_page_url',
      ])
    }
  }

  const TABLE_HEADERS: TableHeaders<GiftCardGroup['giftCards'][number]> = [
    {
      id: 'code',
      label: 'Gift card code',
      render: (giftCard) => (
        <ButtonClean
          href={`${currentShop?.baseUri}/gift_cards/${giftCard.id}`}
          target="_blank"
          label={giftCard.code}
        />
      ),
      width: 6,
    },
    {
      id: 'Used',
      label: 'Used',
      render: (giftCard) => <PriceString {...giftCard.balance.used} />,
      width: 1.5,
    },
    {
      id: 'remaining',
      label: 'Remaining',
      render: (giftCard) => <PriceString {...giftCard.balance.remaining} />,
      width: 1.5,
    },
    {
      id: 'copy',
      label: ' ',
      render: (giftCard) => <CopyButton copyText={giftCard.code} iconSize={16} />,
      width: 0.5,
      alignment: 'right',
    },
  ]

  if (!giftCards) return null

  const renderData = () => {
    if (!media.tablet) {
      return (
        <List
          data={giftCards}
          renderItem={(giftCard: GiftCardGroup['giftCards'][number]) => (
            <div>
              <S.GiftCardCodeWrapper>
                {giftCard.code} <CopyButton copyText={giftCard.code} iconSize={16} />
              </S.GiftCardCodeWrapper>
              <S.GiftCardBalanceWrapper>
                <PriceString {...giftCard.balance.used} /> used /{' '}
                <PriceString {...giftCard.balance.remaining} /> remaining
              </S.GiftCardBalanceWrapper>
            </div>
          )}
        />
      )
    }

    return <Table data={giftCards} headers={TABLE_HEADERS} minWidth={0} compact />
  }

  return (
    <S.Container>
      <S.DataWrapper>{renderData()}</S.DataWrapper>
      <S.Footer>
        <Button
          appearance="secondary"
          label="Copy to clipboard"
          size="small"
          onClick={handleCopyCodes}
        />
        <Button
          appearance="secondary"
          label="Download CSV"
          size="small"
          onClick={handleDownloadCSV}
        />
      </S.Footer>
    </S.Container>
  )
}
